import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One fetch, one validation, one state machine, for each of the four statement views.
 *
 * Every one of them loads a single read-only payload and has the same three states, so the machine
 * lives here rather than four times over. Two things deliberately stay with the caller.
 *
 * The REQUEST, because the typed client knows each route's own parameters and a generic wrapper
 * would have to erase them - the cash-flow view takes a year and the other three take nothing.
 *
 * The VALIDATION, because a payload that arrives reshaped must reach that view's tested error state
 * rather than its render path. This workspace mounts no error boundary, so one unguarded property
 * access on a malformed response replaces the whole Finance route with the router's default error
 * screen instead of the module's own message - which is exactly what `SL-0053` found the dashboard
 * doing.
 */
export type FinanceViewState<TPayload> =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: TPayload; refreshing: boolean };

export type FinanceViewResponse = {
  data?: unknown;
  error?: unknown;
  response: { ok: boolean };
};

export function useFinanceView<TPayload>(
  request: () => Promise<FinanceViewResponse>,
  isValid: (value: unknown) => value is TPayload,
): FinanceViewState<TPayload> & { reload: () => void } {
  const [state, setState] = useState<FinanceViewState<TPayload>>({ status: 'loading' });
  /*
   * Guards against a resolved response from a request the caller has already superseded - the year
   * switched twice in quick succession - which would otherwise overwrite fresher data with older.
   */
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    /*
     * `refreshing` is a FIELD on `ready`, not a fourth state: the year selector lives in the ready
     * branch, and a distinct refreshing state would unmount it at the very click that triggers the
     * refetch.
     */
    setState((current) =>
      current.status === 'ready' ? { ...current, refreshing: true } : { status: 'loading' },
    );

    try {
      const result = await request();
      const payload: unknown = result.data;
      if (result.error !== undefined || !result.response.ok || !isValid(payload)) {
        throw new Error('finance view request failed');
      }
      if (requestRef.current === requestId) {
        setState({ status: 'ready', data: payload, refreshing: false });
      }
    } catch {
      if (requestRef.current === requestId) {
        setState({ status: 'error' });
      }
    }
  }, [request, isValid]);

  useEffect(() => {
    void load();
  }, [load]);

  return { ...state, reload: () => void load() };
}
